<template>
  <VanPopup
    :show="showCustomTone"
    @update:show="emit('update:showCustomTone', $event)"
    teleport="body"
    position="bottom"
    class="px-6 py-4"
    closeable
  >
    <div class="flex flex-col gap-2">
      <!-- 标题和关闭按钮 -->
      <div class="flex justify-between items-center">
        <span class="font-bold">{{
          $t(`User.mate.tone.customTryListen`)
        }}</span>
      </div>
      <!-- 输入框 -->
      <div>
        <label class="block text-base-content/90 mb-1">
          {{ $t(`User.mate.tone.customInputLabel`) }}
        </label>
        <van-field
          v-model="text"
          type="textarea"
          maxlength="200"
          :placeholder="$t('Message.none')"
          class="!text-base-content/90"
          autosize
          show-word-limit
        />
      </div>
      <!-- 试听按钮、进度条、下载按钮 -->
      <MateToneModulePlay
        :mate-tone-id="mateToneId"
        :text="text"
        :mate-tone-config="mateToneConfig"
      />
    </div>
  </VanPopup>

  <VanPopup
    :show="showVoiceSpeed"
    @update:show="emit('update:showVoiceSpeed', $event)"
    teleport="body"
    position="bottom"
    class="px-6 py-4"
    closeable
  >
    <div class="flex flex-col gap-2 pt-1 pb-4">
      <span class="font-bold">{{ $t(`User.mate.tone.speed`) }}</span>
      <div class="flex items-center gap-4 mt-5">
        <span>0.8</span>
        <VanSlider
          :model-value="mateToneConfig.speedRatio"
          @update:model-value="onUpdateSpeedRatio"
          :min="0.8"
          :max="2"
          :step="0.05"
        >
          <template #button>
            <div
              class="w-6 text-primary-content bg-primary text-[10px] text-center rounded-full"
            >
              {{ mateToneConfig.speedRatio }}
            </div>
          </template>
        </VanSlider>
        <span>2</span>
      </div>
    </div>
  </VanPopup>

  <VanPopup
    :show="showEmotion"
    @update:show="emit('update:showEmotion', $event)"
    teleport="body"
    position="bottom"
    class="px-6 py-4"
    closeable
  >
    <div class="flex flex-col gap-2">
      <span class="font-bold">{{ $t(`User.mate.tone.selectEmotion`) }}</span>

      <van-field
        :model-value="
          mateToneConfig!.emotion
            ? $t(`User.mate.tone.emotion.${mateToneConfig!.emotion}`)
            : $t('Message.none')
        "
        is-link
        readonly
        name="picker"
        :label="$t(`User.mate.tone.emotionTitle`)"
        :placeholder="$t('Message.none')"
        @click="showEmotionPicker = true"
      />

      <van-field
        v-if="mateToneConfig!.emotion"
        :model-value="mateToneConfig!.emotionScale"
        is-link
        readonly
        name="picker"
        :label="$t(`User.mate.tone.emotionValue`)"
        :placeholder="$t('Message.none')"
        @click="showEmotionScalePicker = true"
      />
    </div>
  </VanPopup>

  <ModalSelectOption
    v-model:show="showEmotionPicker"
    :options="emotionOptions"
    @confirm="onConfirmEmotion"
  />

  <VanPopup
    v-model:show="showEmotionScalePicker"
    teleport="body"
    position="bottom"
    class="px-6 py-4"
    closeable
  >
    <div class="flex flex-col gap-2 pt-1 pb-4">
      <span class="font-bold">
        {{ $t(`User.mate.tone.selectEmotionValue`) }}
      </span>

      <div class="flex items-center gap-4 mt-5">
        <span>1</span>
        <VanSlider
          :model-value="mateToneConfig.emotionScale"
          @update:model-value="onUpdateEmotionScale"
          :min="1"
          :max="5"
          :step="1"
        >
          <template #button>
            <div
              class="w-6 text-primary-content bg-primary text-[10px] text-center rounded-full"
            >
              {{ mateToneConfig.emotionScale }}
            </div>
          </template>
        </VanSlider>
        <span>5</span>
      </div>
    </div>
  </VanPopup>
</template>

<script setup lang="ts">
import { ToneEmotion } from "@/enums/mateEnums";
import { MateToneConfig } from "@/types/entity";
import { ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  mateToneId: number;
  showCustomTone: boolean;
  showVoiceSpeed: boolean;
  showEmotion: boolean;

  mateToneConfig: MateToneConfig;
}>();

const emit = defineEmits<{
  (e: "update:showCustomTone", show: boolean): void;
  (e: "update:showVoiceSpeed", show: boolean): void;
  (e: "update:showEmotion", show: boolean): void;
}>();

const text = ref("");
const { t } = useI18n();

watch(
  () => props.showVoiceSpeed,
  (val) => {
    const mateToneConfig = props.mateToneConfig;
    if (val && !mateToneConfig.speedRatio) {
      mateToneConfig.speedRatio = 1;
    }
  }
);

const onUpdateSpeedRatio = (value: number) => {
  const mateToneConfig = props.mateToneConfig;
  mateToneConfig.speedRatio = value;
};

const showEmotionPicker = ref(false);
const emotionOptions = ref<{ text: string; value: number }[]>([
  {
    text: t("Message.none"),
    value: 0,
  },
  ...Object.keys(ToneEmotion)
    .filter((v) => !isNaN(Number(v)))
    .map((emotion) => ({
      text: t(`User.mate.tone.emotion.${emotion}`),
      value: Number(emotion),
    })),
]);

const onConfirmEmotion = (value: number) => {
  const mateToneConfig = props.mateToneConfig;
  mateToneConfig.emotion = value;
  if (value === 0) mateToneConfig.emotionScale = undefined;
  else mateToneConfig.emotionScale = 4;
  showEmotionPicker.value = false;
};

const showEmotionScalePicker = ref(false);
const onUpdateEmotionScale = (value: number) => {
  const mateToneConfig = props.mateToneConfig;
  mateToneConfig.emotionScale = Number(value);
};
</script>
